import { Check, ChevronsUpDown, LoaderCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
    Label,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/ui/design-system/components';
import { ALGOLIA_INDEX } from '@/config/constants';
import { createProxySearchClient } from '@/utils/api/algoliaSearchProxy';
import { getPathFromNode } from '@/utils/helpers';

// Per-entityKind copy and the Algolia hit types the widget can point at. The
// ambiguous either/optional kinds fall back to ENTITY_COPY_DEFAULT below.
const ENTITY_COPY = {
    brand: {
        noun: 'brand',
        nounPlural: 'brands',
        label: 'Brand',
        types: ['brand'],
    },
    category: {
        noun: 'category',
        nounPlural: 'categories',
        label: 'Category',
        types: ['category'],
    },
};

const ENTITY_COPY_DEFAULT = {
    noun: 'brand or category',
    nounPlural: 'brands or categories',
    label: 'Entity',
    types: ['brand', 'category'],
};

const entityCopyFor = (entityKind) =>
    ENTITY_COPY[entityKind] ?? ENTITY_COPY_DEFAULT;

// Map an Algolia hit to the stable fields a widget config stores. entity_route is
// built WITHOUT a data_source suffix to match how harvestWidgets stores it (the
// agent's _entity_map route is suffix-less).
const toEntity = (hit) => ({
    entity_id: hit.id ?? hit.objectID,
    entity_type: hit.type,
    entity_route: getPathFromNode(hit, null),
    name: hit.name,
});

// Searchable brand/category picker for the widget edit form. Calls the Algolia
// search proxy directly (no InstantSearch wrapper, no Redux) and returns the
// resolved entity (or null) via onChange. `id`/`label` are overridable so a
// form can render a second instance (the related-posts pair's related entity).
function WidgetEntitySelect({ value, entityKind, onChange, id, label }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const client = useMemo(() => createProxySearchClient(), []);
    const allowedTypes = useMemo(
        () => entityCopyFor(entityKind).types,
        [entityKind],
    );
    // Guards against out-of-order responses clobbering newer results.
    const reqIdRef = useRef(0);

    useEffect(() => {
        const q = query.trim();
        if (!q) {
            setHits([]);
            setIsSearching(false);
            return undefined;
        }
        setIsSearching(true);
        const reqId = ++reqIdRef.current;
        const timer = setTimeout(async () => {
            try {
                const res = await client.search([
                    {
                        indexName: ALGOLIA_INDEX,
                        params: { query: q, hitsPerPage: 8 },
                    },
                ]);
                if (reqId !== reqIdRef.current) return; // stale
                const found = res?.results?.[0]?.hits ?? [];
                setHits(found.filter((h) => allowedTypes.includes(h.type)));
            } catch {
                if (reqId === reqIdRef.current) setHits([]);
            } finally {
                if (reqId === reqIdRef.current) setIsSearching(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [query, client, allowedTypes]);

    const handleSelect = (hit) => {
        onChange(toEntity(hit));
        setOpen(false);
    };

    // When the widget type pins the entity to one kind, label the field for it
    // (Brand / Category); the ambiguous either/optional types keep "Entity".
    const {
        noun: entityNoun,
        nounPlural: entityNounPlural,
        label: kindLabel,
    } = entityCopyFor(entityKind);
    const fieldId = id ?? 'widget-entity';

    return (
        <div className="flex flex-col gap-2">
            <Label
                htmlFor={fieldId}
                className="text-sm font-medium text-foreground"
            >
                {label ?? kindLabel}
            </Label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={fieldId}
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                    >
                        <span
                            className={
                                value?.name
                                    ? 'truncate capitalize'
                                    : 'text-muted-foreground'
                            }
                        >
                            {value?.name || `Search for a ${entityNoun}…`}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={`Search ${entityNoun}…`}
                            value={query}
                            onValueChange={setQuery}
                        />
                        <CommandList>
                            {entityKind === 'optional' && (
                                <CommandItem
                                    value="__none__"
                                    onSelect={() => {
                                        onChange(null);
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={`mr-2 h-4 w-4 ${value ? 'opacity-0' : 'opacity-100'}`}
                                    />
                                    No entity (all categories)
                                </CommandItem>
                            )}
                            <CommandEmpty>
                                {isSearching ? (
                                    <span className="flex items-center justify-center gap-2 text-muted-foreground">
                                        <LoaderCircle className="h-4 w-4 animate-spin" />
                                        Searching…
                                    </span>
                                ) : query.trim() ? (
                                    'No matches.'
                                ) : (
                                    `Type to search ${entityNounPlural}.`
                                )}
                            </CommandEmpty>
                            {hits.map((hit) => (
                                <CommandItem
                                    key={hit.objectID ?? hit.id}
                                    value={`${hit.name}-${hit.objectID ?? hit.id}`}
                                    onSelect={() => handleSelect(hit)}
                                >
                                    <Check
                                        className={`mr-2 h-4 w-4 ${
                                            value?.entity_id ===
                                            (hit.id ?? hit.objectID)
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        }`}
                                    />
                                    <span className="flex flex-col">
                                        <span className="capitalize">
                                            {hit.name}
                                        </span>
                                        <span className="text-[10px] capitalize text-muted-foreground">
                                            {hit.type}
                                        </span>
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}

export default WidgetEntitySelect;
